// ========================================
// pages/ContactUs.js - Contact Information & Form
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 11, 2026
// 
// PURPOSE: Provide multiple ways for users to contact support and the company
// 
// ASSETS USED (from your inventory):
// - /images/symbols/golden-stool.png - Hero badge icon
// - /images/icons/feature-security.png - Contact method icons
// - /images/icons/feature-tradition.png - Contact method icons
// - /images/icons/feature-fraud.png - Contact method icons
// - /images/icons/feature-trustees.png - Contact method icons
// - /images/icons/social-facebook.png, social-twitter.png, etc. - Social media icons
// ========================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const ContactUs = () => {
  // ========================================
  // STATE FOR CONTACT FORM
  // ========================================
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: ''
  });

  // ========================================
  // HANDLE FORM INPUT CHANGES
  // ========================================
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // ========================================
  // HANDLE FORM SUBMISSION
  // ========================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ submitting: true, success: false, error: '' });

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/contact`, formData);
      
      setFormStatus({ submitting: false, success: true, error: '' });
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      setTimeout(() => {
        setFormStatus({ submitting: false, success: false, error: '' });
      }, 5000);
      
    } catch (error) {
      console.error('Contact form error:', error);
      setFormStatus({ 
        submitting: false, 
        success: false, 
        error: error.response?.data?.error || 'Failed to send message. Please try again or email us directly.' 
      });
    }
  };

  return (
    <div className="contact-page">
      
      {/* ======================================== */}
      {/* SECTION 1: HERO */}
      {/* ======================================== */}
      <section className="contact-hero">
        <div className="contact-hero-overlay"></div>
        <div className="contact-container">
          <div className="contact-hero-content">
            <div className="contact-badge">
              <img src="/images/symbols/golden-stool.png" alt="Golden Stool" className="contact-badge-icon" />
              <span>Get in Touch</span>
            </div>
            <h1 className="contact-hero-title">
              We're Here to <span className="gold">Help</span>
            </h1>
            <p className="contact-hero-subtitle">
              Have questions about Agyapadie Vault? Need assistance with your account? 
              Our support team is ready to help you.
            </p>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 2: QUICK CONTACT METHODS */}
      {/* ======================================== */}
      <section className="contact-methods">
        <div className="contact-container">
          <div className="contact-methods-grid">
            
            <div className="contact-method-card">
              <img src="/images/icons/feature-security.png" alt="Email" className="contact-method-icon" />
              <h3>Email Us</h3>
              <p>For general inquiries and support</p>
              <a href="mailto:support@agyapadie.com" className="contact-method-link">support@agyapadie.com</a>
              <span className="contact-method-response">Response within 24 hours</span>
            </div>
            
            <div className="contact-method-card">
              <img src="/images/icons/feature-tradition.png" alt="Phone" className="contact-method-icon" />
              <h3>Call Us</h3>
              <p>For urgent matters</p>
              <a href="tel:+233302123456" className="contact-method-link">+233 (0) 30 212 3456</a>
              <span className="contact-method-response">Mon-Fri, 9am - 5pm GMT</span>
            </div>
            
            <div className="contact-method-card">
              <img src="/images/icons/feature-fraud.png" alt="Office" className="contact-method-icon" />
              <h3>Visit Us</h3>
              <p>Our physical office</p>
              <div className="contact-method-address">
                123 Independence Avenue<br />
                Ridge, Accra<br />
                Ghana
              </div>
              <span className="contact-method-response">By appointment only</span>
            </div>
            
            <div className="contact-method-card">
              <img src="/images/icons/feature-trustees.png" alt="Live Chat" className="contact-method-icon" />
              <h3>Live Chat</h3>
              <p>Instant support</p>
              <button className="contact-method-link contact-chat-btn">Start Live Chat</button>
              <span className="contact-method-response">Available 24/7 (AI-powered)</span>
            </div>
            
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 3: FAQ REFERRAL */}
      {/* ======================================== */}
      <section className="contact-faq-note">
        <div className="contact-container">
          <div className="contact-faq-card">
            <div className="contact-faq-icon">
              <img src="/images/icons/feature-security.png" alt="FAQ" className="contact-faq-img" />
            </div>
            <div className="contact-faq-content">
              <h3>Quick Question?</h3>
              <p>You might find the answer instantly in our Frequently Asked Questions section.</p>
            </div>
            <Link to="/faq" className="contact-faq-btn">View FAQ →</Link>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 4: CONTACT FORM */}
      {/* ======================================== */}
      <section className="contact-form-section">
        <div className="contact-container">
          <div className="contact-form-grid">
            
            {/* Form Side */}
            <div className="contact-form-content">
              <h2 className="contact-section-title">Send Us a <span className="gold">Message</span></h2>
              <p className="contact-section-subtitle">
                Fill out the form below and our team will get back to you within 24 hours.
              </p>
              
              {formStatus.success && (
                <div className="contact-success-message">
                  ✅ Thank you for your message! We'll respond within 24 hours.
                </div>
              )}
              
              {formStatus.error && (
                <div className="contact-error-message">
                  ⚠️ {formStatus.error}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="contact-form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div className="contact-form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                  />
                </div>
                
                <div className="contact-form-group">
                  <label htmlFor="subject">Subject *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a subject</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Account Support">Account Support</option>
                    <option value="Technical Issue">Technical Issue</option>
                    <option value="Billing & Payments">Billing & Payments</option>
                    <option value="Partnership">Partnership Opportunity</option>
                    <option value="Complaint">Complaint</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div className="contact-form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    placeholder="Please provide as much detail as possible..."
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  className="contact-submit-btn"
                  disabled={formStatus.submitting}
                >
                  {formStatus.submitting ? 'Sending...' : 'Send Message'}
                </button>
                
                <p className="contact-form-note">
                  By submitting this form, you agree to our <Link to="/privacy">Privacy Policy</Link>.
                </p>
              </form>
            </div>
            
            {/* Information Side */}
            <div className="contact-info-content">
              <div className="contact-info-card">
                <h3>Response Times</h3>
                <ul className="contact-info-list">
                  <li><strong>Email:</strong> Within 24 hours</li>
                  <li><strong>Phone:</strong> Immediate (during business hours)</li>
                  <li><strong>Live Chat:</strong> Immediate (AI) / 15 mins (human)</li>
                  <li><strong>Urgent Matters:</strong> Call +233 (0) 30 212 3456</li>
                </ul>
              </div>
              
              <div className="contact-info-card">
                <h3>Office Hours</h3>
                <ul className="contact-info-list">
                  <li><strong>Monday - Friday:</strong> 9:00 AM - 5:00 PM GMT</li>
                  <li><strong>Saturday:</strong> 10:00 AM - 2:00 PM GMT</li>
                  <li><strong>Sunday:</strong> Closed</li>
                  <li><strong>Public Holidays:</strong> Closed</li>
                </ul>
              </div>
              
              <div className="contact-info-card">
                <h3>Social Media</h3>
                <div className="contact-social-links">
                  <a href="#" className="contact-social-link">
                    <img src="/images/icons/social-facebook.png" alt="Facebook" />
                  </a>
                  <a href="#" className="contact-social-link">
                    <img src="/images/icons/social-twitter.png" alt="Twitter" />
                  </a>
                  <a href="#" className="contact-social-link">
                    <img src="/images/icons/social-linkedin.png" alt="LinkedIn" />
                  </a>
                  <a href="#" className="contact-social-link">
                    <img src="/images/icons/social-instagram.png" alt="Instagram" />
                  </a>
                </div>
              </div>
              
              <div className="contact-info-card">
                <h3>Emergency Contact</h3>
                <p className="contact-emergency-note">
                  For urgent matters related to death verification or inheritance claims, 
                  please call our emergency line immediately.
                </p>
                <a href="tel:+233501234567" className="contact-emergency-phone">+233 (0) 50 123 4567</a>
                <p className="contact-emergency-hours">Available 24/7</p>
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 5: OFFICE LOCATION / MAP */}
      {/* ======================================== */}
      <section className="contact-map">
        <div className="contact-container">
          <h2 className="contact-section-title">Our <span className="gold">Office Location</span></h2>
          <p className="contact-section-subtitle">
            Visit us at our head office in Accra. Appointments recommended.
          </p>
          
          <div className="contact-map-container">
            <div className="contact-map-placeholder">
              <div className="contact-map-overlay">
                <img src="/images/symbols/golden-stool.png" alt="Location" className="contact-map-icon" />
                <span>📍 123 Independence Avenue, Ridge, Accra, Ghana</span>
                <a 
                  href="https://maps.google.com/?q=123+Independence+Avenue+Ridge+Accra+Ghana" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="contact-map-link"
                >
                  Open in Google Maps →
                </a>
              </div>
            </div>
          </div>
          
          <div className="contact-directions">
            <p>
              <strong>Directions:</strong> From the Independence Square, head north on Independence Avenue. 
              We are located on the right side, opposite the Ghana Revenue Authority building.
            </p>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 6: BUSINESS INQUIRIES */}
      {/* ======================================== */}
      <section className="contact-business">
        <div className="contact-container">
          <div className="contact-business-card">
            <div className="contact-business-icon">
              <img src="/images/icons/feature-trustees.png" alt="Partnerships" className="contact-business-img" />
            </div>
            <div className="contact-business-content">
              <h3>Business & Partnership Inquiries</h3>
              <p>
                Interested in partnering with Agyapadie Vault? Whether you're a financial institution, 
                insurance company, or corporate organization, we'd love to hear from you.
              </p>
              <a href="mailto:partnerships@agyapadie.com" className="contact-business-email">
                partnerships@agyapadie.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 7: CALL TO ACTION */}
      {/* ======================================== */}
      <section className="contact-cta">
        <div className="contact-container">
          <div className="contact-cta-content">
            <h2 className="contact-cta-title">Prefer to Start on Your Own?</h2>
            <p className="contact-cta-subtitle">
              Create your free account in less than 5 minutes. No credit card required.
            </p>
            <div className="contact-cta-buttons">
              <Link to="/register" className="contact-cta-primary">Create Free Account</Link>
              <Link to="/how-it-works" className="contact-cta-secondary">Learn How It Works</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* STYLES */}
      {/* ======================================== */}
      <style dangerouslySetInnerHTML={{ __html: `
        
        .contact-page {
          font-family: 'Montserrat', sans-serif;
          background: white;
        }
        
        .contact-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        .gold {
          color: #FFD700;
        }
        
        /* Hero Section */
        .contact-hero {
          background: #0A0A0A;
          position: relative;
          padding: 80px 0;
          text-align: center;
        }
        
        .contact-hero-overlay {
          position: absolute;
          inset: 0;
          background: url('/images/patterns/hero-pattern.png');
          opacity: 0.05;
          pointer-events: none;
        }
        
        .contact-hero-content {
          position: relative;
          z-index: 2;
        }
        
        .contact-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(255,215,0,0.1);
          padding: 6px 16px;
          border-radius: 30px;
          margin-bottom: 24px;
        }
        
        .contact-badge-icon {
          height: 20px;
          width: auto;
        }
        
        .contact-badge span {
          color: #FFD700;
          font-size: 14px;
        }
        
        .contact-hero-title {
          font-size: 52px;
          font-weight: bold;
          color: white;
          margin-bottom: 20px;
        }
        
        .contact-hero-subtitle {
          font-size: 18px;
          color: #ccc;
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.6;
        }
        
        /* Contact Methods Grid */
        .contact-methods {
          padding: 60px 0;
          background: white;
        }
        
        .contact-methods-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
        }
        
        .contact-method-card {
          background: #F5F0E6;
          padding: 30px;
          border-radius: 15px;
          text-align: center;
          transition: transform 0.3s;
        }
        
        .contact-method-card:hover {
          transform: translateY(-5px);
        }
        
        .contact-method-icon {
          height: 60px;
          width: auto;
          margin-bottom: 15px;
        }
        
        .contact-method-card h3 {
          font-size: 20px;
          margin-bottom: 10px;
        }
        
        .contact-method-card p {
          font-size: 14px;
          color: #666;
          margin-bottom: 15px;
        }
        
        .contact-method-link {
          display: inline-block;
          color: #FFD700;
          text-decoration: none;
          font-weight: bold;
          margin-bottom: 10px;
        }
        
        .contact-method-link:hover {
          text-decoration: underline;
        }
        
        .contact-method-address {
          font-size: 14px;
          color: #444;
          line-height: 1.5;
          margin-bottom: 10px;
        }
        
        .contact-method-response {
          display: block;
          font-size: 12px;
          color: #888;
        }
        
        .contact-chat-btn {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 14px;
        }
        
        /* FAQ Note Section */
        .contact-faq-note {
          padding: 0 0 60px 0;
        }
        
        .contact-faq-card {
          background: #FFD700;
          border-radius: 15px;
          padding: 30px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 20px;
        }
        
        .contact-faq-icon {
          flex-shrink: 0;
        }
        
        .contact-faq-img {
          height: 50px;
          width: auto;
        }
        
        .contact-faq-content {
          flex: 1;
        }
        
        .contact-faq-content h3 {
          font-size: 20px;
          margin-bottom: 5px;
          color: #0A0A0A;
        }
        
        .contact-faq-content p {
          color: #333;
        }
        
        .contact-faq-btn {
          background: #0A0A0A;
          color: white;
          padding: 12px 24px;
          border-radius: 5px;
          text-decoration: none;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .contact-faq-btn:hover {
          background: #333;
        }
        
        /* Contact Form Section */
        .contact-form-section {
          padding: 60px 0;
          background: #F5F0E6;
        }
        
        .contact-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
        }
        
        .contact-section-title {
          font-size: 32px;
          margin-bottom: 15px;
          color: #0A0A0A;
        }
        
        .contact-section-subtitle {
          color: #666;
          margin-bottom: 30px;
        }
        
        .contact-form {
          background: white;
          padding: 30px;
          border-radius: 15px;
        }
        
        .contact-form-group {
          margin-bottom: 20px;
        }
        
        .contact-form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: bold;
          font-size: 14px;
        }
        
        .contact-form-group input,
        .contact-form-group select,
        .contact-form-group textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          font-family: inherit;
        }
        
        .contact-form-group input:focus,
        .contact-form-group select:focus,
        .contact-form-group textarea:focus {
          outline: none;
          border-color: #FFD700;
        }
        
        .contact-submit-btn {
          width: 100%;
          padding: 14px;
          background: #FFD700;
          color: #0A0A0A;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s;
        }
        
        .contact-submit-btn:hover {
          background: #B8860B;
        }
        
        .contact-submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .contact-form-note {
          font-size: 12px;
          color: #888;
          text-align: center;
          margin-top: 15px;
        }
        
        .contact-form-note a {
          color: #FFD700;
          text-decoration: none;
        }
        
        .contact-success-message {
          background: #d1fae5;
          border: 1px solid #10b981;
          color: #065f46;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        
        .contact-error-message {
          background: #fee2e2;
          border: 1px solid #ef4444;
          color: #dc2626;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        
        .contact-info-content {
          display: flex;
          flex-direction: column;
          gap: 25px;
        }
        
        .contact-info-card {
          background: white;
          padding: 25px;
          border-radius: 15px;
        }
        
        .contact-info-card h3 {
          font-size: 18px;
          margin-bottom: 15px;
          color: #0A0A0A;
          border-bottom: 2px solid #FFD700;
          display: inline-block;
          padding-bottom: 5px;
        }
        
        .contact-info-list {
          list-style: none;
        }
        
        .contact-info-list li {
          margin-bottom: 12px;
          font-size: 14px;
          color: #555;
        }
        
        .contact-social-links {
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
        }
        
        .contact-social-link img {
          height: 30px;
          width: auto;
          transition: opacity 0.3s;
        }
        
        .contact-social-link img:hover {
          opacity: 0.7;
        }
        
        .contact-emergency-note {
          font-size: 14px;
          color: #666;
          margin-bottom: 15px;
          line-height: 1.5;
        }
        
        .contact-emergency-phone {
          display: inline-block;
          font-size: 24px;
          font-weight: bold;
          color: #dc2626;
          text-decoration: none;
          margin-bottom: 5px;
        }
        
        .contact-emergency-hours {
          font-size: 12px;
          color: #888;
        }
        
        /* Map Section */
        .contact-map {
          padding: 60px 0;
          background: white;
        }
        
        .contact-map-container {
          margin-top: 30px;
          border-radius: 15px;
          overflow: hidden;
        }
        
        .contact-map-placeholder {
          background: #F5F0E6;
          height: 300px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }
        
        .contact-map-overlay {
          text-align: center;
        }
        
        .contact-map-icon {
          height: 60px;
          width: auto;
          margin-bottom: 15px;
        }
        
        .contact-map-overlay span {
          display: block;
          margin-bottom: 15px;
          color: #666;
        }
        
        .contact-map-link {
          color: #FFD700;
          text-decoration: none;
          font-weight: bold;
        }
        
        .contact-directions {
          margin-top: 20px;
          padding: 15px;
          background: #F5F0E6;
          border-radius: 10px;
          font-size: 14px;
          color: #555;
        }
        
        /* Business Inquiries */
        .contact-business {
          padding: 0 0 60px 0;
        }
        
        .contact-business-card {
          background: linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%);
          border-radius: 15px;
          padding: 40px;
          display: flex;
          align-items: center;
          gap: 30px;
          flex-wrap: wrap;
        }
        
        .contact-business-icon {
          flex-shrink: 0;
        }
        
        .contact-business-img {
          height: 60px;
          width: auto;
        }
        
        .contact-business-content {
          flex: 1;
        }
        
        .contact-business-content h3 {
          font-size: 24px;
          color: white;
          margin-bottom: 10px;
        }
        
        .contact-business-content p {
          color: #ccc;
          margin-bottom: 15px;
        }
        
        .contact-business-email {
          display: inline-block;
          color: #FFD700;
          font-size: 18px;
          text-decoration: none;
          font-weight: bold;
        }
        
        .contact-business-email:hover {
          text-decoration: underline;
        }
        
        /* CTA Section */
        .contact-cta {
          padding: 60px 0;
          background: #F5F0E6;
          text-align: center;
        }
        
        .contact-cta-content {
          max-width: 700px;
          margin: 0 auto;
        }
        
        .contact-cta-title {
          font-size: 32px;
          margin-bottom: 15px;
          color: #0A0A0A;
        }
        
        .contact-cta-subtitle {
          font-size: 16px;
          color: #666;
          margin-bottom: 30px;
        }
        
        .contact-cta-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }
        
        .contact-cta-primary {
          padding: 14px 32px;
          background: #FFD700;
          color: #0A0A0A;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .contact-cta-primary:hover {
          background: #B8860B;
        }
        
        .contact-cta-secondary {
          padding: 14px 32px;
          background: transparent;
          border: 2px solid #0A0A0A;
          color: #0A0A0A;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .contact-cta-secondary:hover {
          background: #0A0A0A;
          color: white;
        }
        
        /* Responsive */
        @media (max-width: 1024px) {
          .contact-methods-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .contact-hero-title {
            font-size: 40px;
          }
        }
        
        @media (max-width: 768px) {
          .contact-hero-title {
            font-size: 28px;
          }
          
          .contact-hero-subtitle {
            font-size: 16px;
          }
          
          .contact-methods-grid {
            grid-template-columns: 1fr;
          }
          
          .contact-form-grid {
            grid-template-columns: 1fr;
          }
          
          .contact-section-title {
            font-size: 24px;
          }
          
          .contact-faq-card {
            flex-direction: column;
            text-align: center;
          }
          
          .contact-business-card {
            text-align: center;
            flex-direction: column;
          }
          
          .contact-cta-title {
            font-size: 24px;
          }
        }
        
      `}} />
      
    </div>
  );
};

export default ContactUs;